import React, { useState, useEffect } from "react";
import { Badge, Button, Col, Container, Row } from "react-bootstrap";
import Confirm from "../Confirm";
import { Link, useLocation, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import moment from "moment";
import WhatsAppChat from "../whatsapp_messenger/WhatsAppChat";
import { ToastContainer, toast } from "react-toastify";
import jwt_decode from "jwt-decode";
import { FaUsers } from "react-icons/fa";

const LeadView = ({ socket, selectedWhatsAppSetting }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [lead, setLead] = useState(location.state || {});
  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(!location.state);
  const [allOwners, setAllOwners] = useState([]);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );

  const formatPhoneNumber = (number) => {
    const shouldHideNumber =
      userInfo?.userrole !== "ADMIN" &&
      userInfo?.is_whatsapp_number_hidden === true;

    if (shouldHideNumber && number) {
      const numberString = String(number);
      if (numberString.length <= 5) return numberString;
      const visibleDigits = numberString.slice(-5);
      const maskedDigits = "X".repeat(numberString.length - 5);
      return `${maskedDigits}${visibleDigits}`;
    }
    return number;
  };

  useEffect(() => {
    // Only fetch if we don't have the data in location.state
    if (id) {
      fetchLead();
    }
  }, [id, selectedWhatsAppSetting]);

  // Extract all owners from lead data
  useEffect(() => {
    if (lead && lead.all_owner_names) {
      setAllOwners(lead.all_owner_names || []);
    } else if (lead && lead.ownerid) {
      // If no all_owner_names but we have ownerid, create a basic array
      setAllOwners([{ id: lead.ownerid, name: lead.ownername }]);
    } else {
      setAllOwners([]);
    }
  }, [lead]);

  const fetchLead = async () => {
    try {
      setIsLoading(true);
      const result = await WhatsAppAPI.fetchLeadById(id);
      if (result.success) {
        // Add formatted phone number to the lead data
        const formattedLead = {
          ...result.records,
          display_whatsapp_number: formatPhoneNumber(
            result.records.whatsapp_number,
          ),
        };
        setLead(formattedLead);

        // If no all_owner_names in response but we have ownerids, fetch them
        if (
          !formattedLead.all_owner_names &&
          formattedLead.ownerids &&
          formattedLead.ownerids.length > 0
        ) {
          try {
            const ownersResult = await WhatsAppAPI.fetchUsersByIds(
              formattedLead.ownerids,
            );
            if (ownersResult.success) {
              formattedLead.all_owner_names = ownersResult.records.map(
                (user) => ({
                  id: user.id,
                  name:
                    `${user.firstname || ""} ${user.lastname || ""}`.trim() ||
                    user.username,
                }),
              );
              setLead(formattedLead);
            }
          } catch (ownerError) {
            console.error("Error fetching owner details:", ownerError);
          }
        }
      } else {
        toast.error("Failed to fetch lead details");
        navigate("/leads");
      }
    } catch (error) {
      console.error("Error fetching lead:", error);
      toast.error("Error loading lead details");
      navigate("/leads");
    } finally {
      setIsLoading(false);
    }
  };

  const deleteLead = async () => {
    try {
      const result = await WhatsAppAPI.deleteLead(lead.id);
      if (result.success) {
        toast.success("Lead deleted successfully");
        navigate("/leads");
      } else {
        toast.error(result.message || "Failed to delete lead");
      }
    } catch (error) {
      console.error("Error deleting lead:", error);
      toast.error("Error deleting lead");
    }
  };

  const editLead = (lead) => {
    navigate(`/leads/${lead.id}/e`, { state: lead });
  };

  const handleBack = () => {
    navigate("/leads");
  };

  if (isLoading) {
    return (
      <Container className="mt-5">
        <Row className="mx-5">
          <Col className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading lead details...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <>
      <Container className="mt-5">
        <Row className="mx-5 section-header">
          <Col lg={12} sm={12} xs={12}>
            <Row className="view-form-header align-items-center">
              <Col lg={8} sm={8} xs={8} className="text-capitalize">
                Lead
                <h5>
                  {lead.firstname ? lead.firstname + " " : ""}
                  {lead.lastname || ""}
                </h5>
              </Col>
              <Col lg={4} sm={4} xs={4} className="text-end">
                <Button
                  className="mx-2 btn-sm"
                  variant="outline-light"
                  onClick={handleBack}
                >
                  Back
                </Button>
                {!(
                  userInfo?.userrole !== "ADMIN" &&
                  userInfo?.is_whatsapp_number_hidden === true
                ) && (
                  <Button
                    className="mx-2 btn-sm"
                    variant="light"
                    onClick={() => editLead(lead)}
                  >
                    Edit
                  </Button>
                )}

                <Button
                  className="btn-sm mx-2"
                  variant="danger"
                  onClick={() => setModalShow(true)}
                >
                  Delete
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <Container className="mt-1">
        <Row className="mx-5 view-form">
          <Col lg={12} sm={12} xs={12}>
            <Row className="py-2 ibs-edit-form">
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Lead Name</label>
                <span className="text-capitalize">
                  {lead.firstname || lead.lastname
                    ? `${lead.firstname || ""} ${lead.lastname || ""}`
                    : "\u00A0"}
                </span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>WhatsApp Number</label>
                <span>
                  {lead.whatsapp_number
                    ? `${lead.country_code}${
                        lead.display_whatsapp_number ||
                        formatPhoneNumber(lead.whatsapp_number)
                      }`
                    : "\u00A0"}
                </span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Email</label>
                <span>{lead.email || "\u00A0"}</span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Lead Source</label>
                <span>{lead.leadsource || "\u00A0"}</span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Lead Status</label>
                <span>{lead.leadstatus || "\u00A0"}</span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Date of Birth</label>
                <span>
                  {lead.dob
                    ? moment(lead.dob).format("DD MMM, YYYY")
                    : "\u00A0"}
                </span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Address</label>
                <span>{lead.address || "\u00A0"}</span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Description</label>
                <span>{lead.description || "\u00A0"}</span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Tags</label>
                <span>
                  {lead.tag_names && lead.tag_names.length > 0
                    ? lead.tag_names.map((tag, index) => (
                        <Badge
                          key={index}
                          bg="light"
                          style={{ display: "inline" }}
                          className="me-2 px-2 text-muted text-capitalize"
                        >
                          {tag.name}
                        </Badge>
                      ))
                    : "\u00A0"}
                </span>
              </Col>

              {/* All Assigned Owners */}
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>
                  <FaUsers className="me-2" />
                  All Assigned Owners ({allOwners.length})
                </label>
                <div className="mt-2">
                  {allOwners.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {allOwners.map((owner, index) => (
                        <Badge
                          key={index}
                          bg={index === 0 ? "warning" : "info"}
                          className="px-3 py-2 d-flex text-black align-items-center"
                          title="Click to view profile"
                        >
                          {owner.name}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted">No owners assigned</span>
                  )}
                </div>
              </Col>

              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Created Date</label>
                <span>
                  {lead.createddate
                    ? moment(lead.createddate).format("DD MMM, YYYY")
                    : "\u00A0"}
                </span>
              </Col>
              <Col lg={6} sm={6} xs={6} className="mb-2">
                <label>Last Modified</label>
                <span>
                  {lead.lastmodifieddate ? (
                    <>
                      {moment(lead.lastmodifieddate).format("DD MMM, YYYY")}
                      {lead.lastmodifiedbyname && (
                        <Badge
                          bg="primary"
                          className="mx-2"
                          style={{ display: "inline" }}
                        >
                          by {lead.lastmodifiedbyname}
                        </Badge>
                      )}
                    </>
                  ) : (
                    "\u00A0"
                  )}
                </span>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      {lead && lead.id && lead.whatsapp_number && (
        <Container className="mb-5 mt-2">
          <Row className="g-0 mx-5">
            <Col lg={12} sm={12} xs={12} className="mb-4">
              <WhatsAppChat
                userDetail={lead}
                socket={socket}
                selectedWhatsAppSetting={selectedWhatsAppSetting}
              />
            </Col>
          </Row>
        </Container>
      )}

      {modalShow && (
        <Confirm
          show={modalShow}
          onHide={() => setModalShow(false)}
          deleteLead={deleteLead}
          title="Confirm delete?"
          message="You are going to delete the record. Are you sure?"
          table="lead"
        />
      )}

      <ToastContainer />
    </>
  );
};

export default LeadView;
